import type { ImportApiError, ImportClient, PaginatedResponse, PaginationParams } from "@prequel/import-react";
import { createHttpClient } from "@prequel/import-react";

import { type AppError, getAxiosEnvironment, handle401 } from "@/api/client";
import { env } from "@/env.js";

const baseUrl = env.VITE_APP_API_SERVER;
if (!baseUrl || !URL.canParse(baseUrl)) {
  throw new Error(`VITE_APP_API_SERVER must be a valid URL, got: ${baseUrl ?? "undefined"}`);
}

const sdkImportClient = createHttpClient({
  baseUrl,
  credentials: "include",
  headers: () => ({
    Environment: getAxiosEnvironment(),
  }),
  // Session expiry has to behave the same on import routes as everywhere else. The axios
  // response interceptor owns this for the export stack; handle401 is the same handler
  // QueryProvider registers, read through a live binding so it picks up registration.
  onUnauthorized: () => handle401?.(),
});

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

const isImportApiError = (error: unknown): error is ImportApiError =>
  isObject(error) && typeof error.message === "string" && typeof error.statusCode === "number";

// Keeps the flat SDK fields alongside the envelope: the hooks re-normalize thrown errors
// with toImportApiError, which drops the message unless it recognizes the shape.
export const toAppError = (error: ImportApiError): AppError & ImportApiError => ({
  ...error,
  error: {
    message: error.message,
    statusCode: error.statusCode,
    suppressGlobalNotification: false,
  },
});

async function withAppError<T>(request: Promise<T>): Promise<T> {
  try {
    return await request;
  } catch (error) {
    if (isImportApiError(error)) {
      throw toAppError(error);
    }
    throw error;
  }
}

export const importClient: ImportClient = {
  get: <T>(path: string): Promise<T> => withAppError(sdkImportClient.get<T>(path)),
  getPaginated: <T>(
    path: string,
    params?: PaginationParams,
    queryParams?: Record<string, string | undefined>,
  ): Promise<PaginatedResponse<T>> => withAppError(sdkImportClient.getPaginated<T>(path, params, queryParams)),
  post: <T>(path: string, body?: unknown): Promise<T> => withAppError(sdkImportClient.post<T>(path, body)),
  patch: <T>(path: string, body?: unknown): Promise<T> => withAppError(sdkImportClient.patch<T>(path, body)),
  delete: (path: string): Promise<void> => withAppError(sdkImportClient.delete(path)),
};
